import React, { useContext, useEffect, useMemo, useState } from "react";
import styles from "./styles/AssetDashboard.module.css";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import { UserData } from "../../../Context/DataContext";
import Dropdown from "../Asset Evaluation/Dropdown";
import ReportDateField from "./ReportDateField";
import {
  FINANCIAL_EXPOSURE_OPTIONS,
  FINANCIAL_EXPOSURE_CUSTOM,
  CURRENCY_OPTIONS,
  defaultCurrencyFor,
} from "../Asset Evaluation/AssetEvaluation";
import {
  resolveExposureSelection,
  toStoredDate,
  appendEvaluationRecords,
} from "../../../Utils/assetEvaluationUtils";
import { RandomId } from "../../../Utils/RandomId";

/*
  Giving an unvalued asset its first value, from the report.

  This is the top block of the Add / Edit Asset panel — the financial exposure
  and the reading that goes with it — on its own, opened from a "No value" row.
  The rules are the panel's, restated here rather than shared because the panel
  owns a whole evaluation form and this owns four fields:

    - an exposure is required, because a value on a date means nothing without
      saying what was valued;
    - "Custom" is a prompt for a name, not a type, so an unnamed one is refused;
    - a reading is all-or-nothing: date, amount and currency together.

  On save the asset gains a record through `appendEvaluationRecords`, the same
  function the asset modal uses, so a value entered here is indistinguishable
  from one entered there.
*/

const FALLBACK = {
  assetDashboardText93: "Add a value",
  assetDashboardText94: "Save",
  assetDashboardText95: "Cancel",
  assetDashboardText96: "Please complete the highlighted fields",
};

function ValuationModal({ asset, onClose }) {
  const userData = UserData();
  const websiteCurrentLang = useContext(LanguageContext).language;

  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.AssetDashboard?.AssetDashboard || FALLBACK
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.AssetDashboard?.AssetDashboard || FALLBACK
    );
  }, [websiteCurrentLang]);
  const t = (key) => (content && content[key]) || FALLBACK[key];

  // The panel's own wording for the exposure field and its options.
  const evalBlock = websiteCurrentLang?.content?.User?.EditAsset?.AssetEvaluation;
  const tEval = (key, fallback) => (evalBlock && evalBlock[key]) || fallback;

  const root = (userData && userData.UserData) || {};
  const customOptions = root.financial_exposure_custom_options || [];

  /*
    An asset valued in another period already has an exposure and a currency
    settled — it is only this window that has no reading. Those come prefilled
    so the user is asked for what is actually missing, the date and the amount,
    rather than made to restate what the asset already says about itself.

    The reading itself is never prefilled: it is a new reading, and carrying the
    last one over would invite saving the same figure again under a new date.
  */
  const [exposure, setExposure] = useState(asset?.financial_exposure || "");
  const [customLabel, setCustomLabel] = useState("");
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(
    asset?.currency || defaultCurrencyFor(websiteCurrentLang?.label) || "eur"
  );
  const [errors, setErrors] = useState({});

  const isCustom = exposure === FINANCIAL_EXPOSURE_CUSTOM;

  const exposureGroups = useMemo(
    () => [
      {
        label: "",
        options: [
          { value: "", label: tEval("assetEvaluationText16", "Select") },
          ...FINANCIAL_EXPOSURE_OPTIONS.map((o) => ({
            value: o.value,
            label: tEval(o.labelKey, o.value),
          })),
          ...customOptions.map((o) => ({ value: o.value, label: o.label })),
          {
            value: FINANCIAL_EXPOSURE_CUSTOM,
            label: tEval("assetEvaluationText24", "Custom"),
          },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [evalBlock, customOptions]
  );

  const blank = (v) => String(v ?? "").trim() === "";

  const save = () => {
    /*
      The panel's rules, in the panel's order: the exposure first, then the
      reading as a whole. Everything missing is reported at once rather than one
      field per attempt.
    */
    const next = {};
    if (blank(exposure)) next.exposure = true;
    if (isCustom && blank(customLabel)) next.customLabel = true;
    if (blank(date)) next.date = true;
    if (blank(amount)) next.amount = true;
    if (blank(currency)) next.currency = true;
    setErrors(next);
    if (Object.keys(next).length) return;

    // A typed custom name becomes one of this user's own exposure types.
    const resolved = resolveExposureSelection(
      { financial_exposure: exposure, financial_exposure_custom_label: customLabel },
      customOptions
    );

    const records = appendEvaluationRecords(asset, {
      evaluation_date: toStoredDate(date),
      asset_value: amount,
      currency,
    });

    const updated = {
      ...asset,
      financial_exposure: resolved.financial_exposure,
      evaluation_date: toStoredDate(date),
      asset_value: amount,
      currency,
      asset_evaluation_records: records.asset_evaluation_records,
    };

    userData.setUserData({
      ...root,
      assets: (root.assets || []).map((a) =>
        a.asset_id === asset.asset_id ? updated : a
      ),
      financial_exposure_custom_options: resolved.options,
    });
    userData.Update(RandomId(10));
    onClose();
  };

  const invalid = (key) => (errors[key] ? styles.vm_invalid : "");

  return (
    <div className={styles.vm_backdrop} onClick={onClose}>
      <div
        className={styles.vm_modal}
        role="dialog"
        aria-label={t("assetDashboardText93")}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className={styles.vm_title}>{t("assetDashboardText93")}</h3>
        <div className={styles.vm_asset}>{asset?.asset_name}</div>

        <div className={styles.vm_field}>
          <div className={styles.vm_label}>
            {tEval("assetEvaluationText5", "Financial Exposure")}
          </div>
          {isCustom ? (
            <div className={styles.vm_custom}>
              <input
                autoFocus
                type="text"
                className={`${styles.vm_control} ${invalid("customLabel")}`}
                placeholder={tEval("assetEvaluationText26", "Enter a name")}
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
              />
              {/* Back to the list, discarding the name. */}
              <button
                type="button"
                className={styles.vm_back}
                title={tEval("assetEvaluationText16", "Select")}
                onClick={() => {
                  setExposure("");
                  setCustomLabel("");
                }}
              >
                ↩
              </button>
            </div>
          ) : (
            <Dropdown
              value={exposure}
              onChange={setExposure}
              groups={exposureGroups}
              placeholder={tEval("assetEvaluationText16", "Select")}
              triggerClassName={`${styles.vm_control} ${invalid("exposure")}`}
              maxMenuHeight={230}
              ariaLabel={tEval("assetEvaluationText5", "Financial Exposure")}
            />
          )}
        </div>

        <div className={styles.vm_row}>
          <div className={styles.vm_field}>
            <div className={styles.vm_label}>
              {tEval("assetEvaluationText6", "Date")}
            </div>
            {/*
              Ours, not the browser's -- a native date input reads DD/MM even
              with the site in English. Same control class, same box.
            */}
            <ReportDateField
              className={`${styles.vm_control} ${invalid("date")}`}
              value={date}
              onChange={setDate}
              french={websiteCurrentLang?.label === "fr"}
              ariaLabel={tEval("assetEvaluationText6", "Date")}
            />
          </div>

          <div className={styles.vm_field}>
            <div className={styles.vm_label}>
              {tEval("assetEvaluationText7", "Asset Value Today")}
            </div>
            <input
              type="text"
              inputMode="decimal"
              className={`${styles.vm_control} ${invalid("amount")}`}
              value={amount}
              onChange={(e) =>
                setAmount(e.target.value.replace(/[^\d.]/g, ""))
              }
            />
          </div>

          <div className={`${styles.vm_field} ${styles.vm_field_currency}`}>
            <div className={styles.vm_label}>
              {tEval("assetEvaluationText8", "Currency")}
            </div>
            <select
              className={`${styles.vm_control} ${invalid("currency")}`}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {CURRENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {Object.keys(errors).length > 0 && (
          <div className={styles.vm_error}>{t("assetDashboardText96")}</div>
        )}

        <div className={styles.vm_actions}>
          <button type="button" className={styles.vm_cancel} onClick={onClose}>
            {t("assetDashboardText95")}
          </button>
          <button type="button" className={styles.vm_save} onClick={save}>
            {t("assetDashboardText94")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ValuationModal;
